import { equals } from "angular";
import { Rect } from "konva/lib/shapes/Rect";
import { Line } from "konva/lib/shapes/Line";
import { ISize, ISizeableState, IFontState, IHasBackgroundColor, IHasTextAlignment, IShapeReactionState } from "./util";
import { fireStateEvent } from "../events";
import { darken } from "../util";
import { WhiteboardShapeStateType } from ".";
import { WhiteboardShape, IShapeState } from "./shape";
import { IWhiteboardRendererContext } from "../whiteboard-renderer";
import { isDarkColor } from "../behaviors/shape-context-menu";
import { Text } from "konva/lib/shapes/Text";
import { inlineEdit } from "../behaviors/inline-edit";
import { voting } from "../behaviors/voting";
import { localize } from "@gtmhub/localization";

export interface INoteState extends IShapeState, ISizeableState, IFontState, IShapeReactionState, IHasBackgroundColor, IHasTextAlignment {
  type: "note";
  content: string;
}

const smallRectHeight = 15;
export const contentPadding = 15;

/**
 * The note is split into three parts: a big rectangle, a small
 * rectangle and a triangle (the angle), so that we can render the
 * desired shape.
 * ___________
 * |         |
 * |         |
 * |_________|
 * |________|/
 */
export class Note extends WhiteboardShape<INoteState> {
  private contentNode;
  private padNodes: { big: Rect; small: Rect; angle: Line };
  private placeholder: string;
  static stateTypes: WhiteboardShapeStateType[] = ["note"];

  constructor(state: INoteState, context: IWhiteboardRendererContext) {
    super(state, context);

    this.placeholder = `${localize("start_typing")}...`;

    this.on("transform", () => this.transform());

    this.pad();
    this.content(state);
    this.resize(state.size);
    this.backgroundColor(state);

    this.context.events.onReactionSettingsChanged((args) => {
      this.context.reactionSettings = args.payload;
    });

    if (this.context.whiteboardVotingEnabled) {
      voting(this, this.context);
    }
  }

  updateState(state: INoteState): boolean {
    let updated = false;
    if (
      this.state.content !== state.content ||
      this.state.fontSize != state.fontSize ||
      this.state.fontFamily !== state.fontFamily ||
      this.state.bold !== state.bold ||
      this.state.italic !== state.italic ||
      this.state.strikeThrough !== state.strikeThrough ||
      this.state.underline !== state.underline ||
      this.state.textAlignment !== state.textAlignment ||
      this.state.backgroundColor !== state.backgroundColor
    ) {
      this.content(state);
      updated = true;
    }
    if (!equals(this.state.position, state.position) || !equals(this.state.size, state.size)) {
      this.position(state.position);
      this.resize(state.size);
      updated = true;
    }
    if (this.state.backgroundColor !== state.backgroundColor) {
      this.backgroundColor(state);

      updated = true;
    }
    this.state = state;

    if (this.context.whiteboardVotingEnabled) {
      voting(this, this.context);
    }
    return updated;
  }

  private pad() {
    const shadow = {
      shadowColor: "#C8D1DF",
      shadowBlur: 3,
      shadowOpacity: 0.7,
      shadowOffset: { x: 0, y: 3 },
    };

    this.padNodes = {
      big: new Rect({ ...shadow }),
      small: new Rect({ ...shadow }),
      angle: new Line({
        points: [],
        closed: true,
        ...shadow,
      }),
    };
    this.add(this.padNodes.big, this.padNodes.small, this.padNodes.angle);
  }

  private content(state: INoteState) {
    if (!this.contentNode) {
      this.contentNode = new Text({
        lineHeight: 1.4,
      });

      inlineEdit(this, this.contentNode, {
        multiline: true,
        autoGrow: true,
        placeholder: this.placeholder,
        whiteboardBasicShapesEnabled: this.context.whiteboardBasicShapesEnabled,
        onUpdate: (evt, reason, value) => {
          if (reason === "outsideClick") {
            value = value.trim();
            if (value === this.placeholder) {
              value = "";
            }

            if (value !== this.state.content) {
              fireStateEvent("stateChange", evt, this, { state: this.state, changes: { content: value } });
            }
          }
        },
      });

      this.add(this.contentNode);
    }

    this.contentNode.setAttrs({
      text: state.content || this.placeholder,
      fontSize: state.fontSize,
      fontFamily: state.fontFamily,
      fontStyle: [state.bold ? "bold" : "", state.italic ? "italic" : ""].join(" ").trim(),
      textDecoration: [state.strikeThrough ? "line-through" : "", state.underline ? "underline" : ""].join(" ").trim(),
      align: state.textAlignment,
      verticalAlign: "top",
      fill: state.backgroundColor && isDarkColor(state.backgroundColor) ? "#ffffff" : "#384758",
    });
  }

  private backgroundColor(state: INoteState) {
    this.padNodes.big.fill(state.backgroundColor);
    this.padNodes.small.fill(state.backgroundColor);
    this.padNodes.angle.fill(state.backgroundColor ? darken(state.backgroundColor, 0.2) : null);
  }

  private transform() {
    const size: ISize = {
      width: this.scaleX() * this.state.size.width,
      height: Math.abs(this.scaleY()) * this.state.size.height,
    };
    this.scale({ x: 1, y: 1 }).resize(size);
  }

  private resize(size: ISize) {
    const bigRectHeight = size.height - smallRectHeight;
    this.padNodes.big.size({ width: size.width, height: bigRectHeight + 1 }); /* fix for the light border appearing over the small quadrant */
    this.padNodes.small.size({ width: size.width - smallRectHeight, height: smallRectHeight }).y(bigRectHeight);
    this.padNodes.angle.points([this.padNodes.small.width(), this.padNodes.small.y(), size.width, this.padNodes.small.y(), this.padNodes.small.width(), size.height]);

    this.contentNode.size({ width: size.width - contentPadding * 2, height: size.height - contentPadding * 2 }).position({ x: contentPadding, y: contentPadding });
  }
}
Note.prototype.selectionOptions = { minSize: 70, resizable: true };
